import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import FormRow from '../../../app/components/FormRow';
import DictionarySelector from '../Selects/DictionarySelector';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { OptionWithLang } from '../Selects/Selector';
import {
  GetDictionaryDto,
  GetUmpPositionDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { ImportTeamField } from '../Estimates/ImportTeamField';

export interface UmpNewPathPhase {
  id: string | undefined;
  value?: string;
  valueEn?: string;
}

export interface UmpNewSpecialistTeam {
  id: string | undefined;
  name?: string;
}

interface UmpParameterMapperFormProps {
  umpPosition: GetUmpPositionDto;
  companyId?: string;
  pathPhaseDictionaries?: GetDictionaryDto[];
  pathPhaseEnabled?: boolean;
  handleUpdate?: (
    uid: string,
    newPathPhaseDict: UmpNewPathPhase | undefined,
    newSpecialistTeam: UmpNewSpecialistTeam | undefined,
  ) => void;
}

export const UmpParameterMapperForm: React.FC<
  UmpParameterMapperFormProps
> = props => {
  const [newPathPhaseDict, setNewPathPhaseDict] = useState<
    UmpNewPathPhase | undefined
  >({
    id: props.umpPosition.pathPhaseDict?.id,
    value: props.umpPosition.pathPhaseDict?.value,
    valueEn: props.umpPosition.pathPhaseDict?.valueEn,
  });
  const [newSpecialistTeam, setNewSpecialistTeam] = useState<
    UmpNewSpecialistTeam | undefined
  >({
    id: props.umpPosition.specialistTeamId ?? props.umpPosition.deliveryTeamId,
    name:
      props.umpPosition.specialistTeamName ??
      props.umpPosition.deliveryTeamName,
  });
  const [show, setShow] = useState(false);
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    setNewPathPhaseDict(
      props.umpPosition.pathPhaseDict
        ? {
            id: props.umpPosition.pathPhaseDict?.id,
            value: props.umpPosition.pathPhaseDict?.value,
            valueEn: props.umpPosition.pathPhaseDict?.valueEn,
          }
        : undefined,
    );
    setNewSpecialistTeam(
      props.umpPosition.specialistTeamId || props.umpPosition.deliveryTeamId
        ? {
            id:
              props.umpPosition.specialistTeamId ??
              props.umpPosition.deliveryTeamId,
            name:
              props.umpPosition.specialistTeamName ??
              props.umpPosition.deliveryTeamName,
          }
        : undefined,
    );
  }, [props.umpPosition, show]);

  const updateParameter = () => {
    props.handleUpdate?.(
      props.umpPosition.uid,
      newPathPhaseDict,
      newSpecialistTeam,
    );
    setShow(false);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShow(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShow(true);
  };

  return (
    <>
      <OverlayTrigger
        overlay={<Tooltip>{t(tk.umpImport.changeParam)}</Tooltip>}
        trigger={['hover', 'hover']}
      >
        <Button className="py-0 btn-link btn-transparent" onClick={handleClick}>
          <Icon.ArrowRepeat size={18} className="icon-in-cell" />
        </Button>
      </OverlayTrigger>
      {show && (
        /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
        <div onClick={e => e.stopPropagation()}>
          <Modal
            onHide={() => setShow(false)}
            show={show}
            centered={true}
            keyboard={true}
            backdrop="static"
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>{t(tk.umpImport.chooseParam)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form className="d-grid gap-3">
                {props.pathPhaseEnabled &&
                  (props.pathPhaseDictionaries?.length || 0) > 0 && (
                    <FormRow
                      controlId="pathPhase"
                      label={t(tk.common.positionPhase)}
                    >
                      <DictionarySelector
                        dictionary={'Faza'}
                        value={newPathPhaseDict?.id || ''}
                        allowableValues={props.pathPhaseDictionaries || []}
                        onChange={(option: OptionWithLang) =>
                          setNewPathPhaseDict({
                            id: option.value,
                            value: option.labelPl,
                            valueEn: option.labelEn,
                          })
                        }
                      />
                    </FormRow>
                  )}
                {props.umpPosition.positionNamePok && (
                  <ImportTeamField
                    positionSpecialistTeams={
                      props.umpPosition.positionSpecialistTeams
                    }
                    companyId={props.companyId}
                    pathPhaseDictionaries={props.pathPhaseDictionaries}
                    pathPhaseDictionaryId={newPathPhaseDict?.id}
                    teamId={newSpecialistTeam?.id}
                    onTeamChange={setNewSpecialistTeam}
                    onPathPhaseChange={setNewPathPhaseDict}
                  />
                )}
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={e => handleClose(e)}>
                {t(tk.button.close)}
              </Button>
              <Button variant="primary" onClick={updateParameter}>
                {t(tk.button.save)}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </>
  );
};
